import { useEffect, useMemo, useState } from "react";
import type { DataSnapshot } from "../lib/types";
import { useRoute, navigateToMonitor } from "../router";
import { useFilters } from "../store/filters";
import { useRefreshData, useScoreAction } from "../hooks/useActions";
import { usePipeline } from "../store/pipeline";
import { useToast } from "../store/toast";
import { fetchApiHealth } from "../lib/live-api";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../lib/query-client";

interface Props {
  data: DataSnapshot;
  open: boolean;
  onClose: () => void;
}

type Item = {
  id: string;
  label: string;
  hint?: string;
  run: () => void;
};

export function CommandPalette({ data, open, onClose }: Props) {
  const [q, setQ] = useState("");
  const [, navigate] = useRoute();
  const select = useFilters((s) => s.select);
  const setSearch = useFilters((s) => s.setSearch);
  const applyPreset = useFilters((s) => s.applyPreset);
  const toast = useToast((s) => s.push);
  const setRun = usePipeline((s) => s.setRun);
  const refresh = useRefreshData();
  const score = useScoreAction();

  const { data: health } = useQuery({
    queryKey: queryKeys.health,
    queryFn: fetchApiHealth,
    staleTime: 120_000,
    enabled: open,
  });
  const actionsReady = health?.actionsReady === true;

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const items = useMemo(() => {
    const list: Item[] = [
      {
        id: "nav-monitor",
        label: "Go to Monitor",
        run: () => { navigate({ name: "monitor" }); onClose(); },
      },
      {
        id: "nav-dashboard",
        label: "Go to Dashboard",
        run: () => { navigate({ name: "dashboard" }); onClose(); },
      },
      {
        id: "nav-insights",
        label: "Go to Insights",
        run: () => { navigate({ name: "insights" }); onClose(); },
      },
      {
        id: "nav-workbench",
        label: "Go to Workbench",
        run: () => { navigate({ name: "workbench" }); onClose(); },
      },
      {
        id: "nav-graph",
        label: "Go to Graph (knowledge map)",
        run: () => { navigate({ name: "graph" }); onClose(); },
      },
      {
        id: "nav-runs",
        label: "Go to Runs (audit trail)",
        run: () => { navigate({ name: "runs" }); onClose(); },
      },
      {
        id: "filter-below85",
        label: "Filter · below 85% combined",
        hint: `${data.skills.filter((s) => s.combinedPercentage < 85).length} skills`,
        run: () => {
          applyPreset("below85");
          navigateToMonitor();
          onClose();
        },
      },
      {
        id: "filter-critical",
        label: "Filter · critical status",
        run: () => {
          applyPreset("critical");
          navigateToMonitor();
          onClose();
        },
      },
    ];

    if (actionsReady) {
      list.push({
        id: "score-missing",
        label: "Score missing LLM (workflow)",
        run: () => {
          void score.mutateAsync({ mode: "missing" }).then((r) => {
            if (r.run?.id) setRun(r.run.id, "Score missing");
            toast("Score missing dispatched", { kind: "info", href: r.run?.html_url });
          }).catch((e) => toast(e instanceof Error ? e.message : "Failed", { kind: "error" }));
          onClose();
        },
      });
      list.push({
        id: "refresh-data",
        label: "Refresh data (GitHub workflow)",
        run: () => {
          void refresh.mutateAsync().then((r) => {
            if (r.run?.id) setRun(r.run.id, "Refresh data");
            toast("Refresh dispatched", { kind: "info" });
          }).catch((e) => toast(e instanceof Error ? e.message : "Failed", { kind: "error" }));
          onClose();
        },
      });
    }

    const needle = q.trim().toLowerCase();
    const skillItems: Item[] = data.skills
      .filter((s) => !needle || s.name.includes(needle) || s.description.toLowerCase().includes(needle))
      .slice(0, 12)
      .map((s) => ({
        id: `skill-${s.name}`,
        label: s.name,
        hint: `${s.combinedPercentage}% · ${s.tier}`,
        run: () => {
          select(s.name);
          setSearch("");
          navigateToMonitor({ skill: s.name });
          onClose();
        },
      }));

    const navFiltered = list.filter(
      (i) => !needle || i.label.toLowerCase().includes(needle) || i.hint?.toLowerCase().includes(needle),
    );

    return [...skillItems, ...navFiltered];
  }, [q, data.skills, actionsReady, navigate, onClose, select, setSearch, score, refresh, setRun, toast]);

  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [q, open]);

  if (!open) return null;

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter" && items[active]) {
      e.preventDefault();
      items[active].run();
    }
  }

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh] px-4 bg-[rgba(10,10,10,0.45)]"
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      onClick={onClose}
    >
      <div
        className="w-full max-w-xl border border-fg bg-bg shadow-[8px_8px_0_0_var(--fg)]"
        onClick={(e) => e.stopPropagation()}
      >
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Search skills or actions…"
          className="w-full border-0 border-b border-fg px-4 py-3 font-mono text-[13px] outline-none bg-bg"
        />
        <ul className="max-h-[320px] overflow-y-auto" role="listbox">
          {items.length === 0 ? (
            <li className="px-4 py-6 font-mono text-[11px] text-muted uppercase">// No matches</li>
          ) : (
            items.map((item, idx) => (
              <li key={item.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={idx === active}
                  className={`w-full text-left px-4 py-2.5 font-mono text-[12px] flex justify-between gap-2 border-b border-rule ${
                    idx === active ? "bg-fg text-bg" : "hover:bg-[var(--hover)]"
                  }`}
                  onMouseEnter={() => setActive(idx)}
                  onClick={() => item.run()}
                >
                  <span>{item.label}</span>
                  {item.hint && (
                    <span className={idx === active ? "text-bg/70" : "text-muted"}>{item.hint}</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
        <div className="px-4 py-2 font-mono text-[9px] uppercase text-muted tracking-[0.14em]">
          ↑↓ navigate · enter run · esc close · ⌘⇧K / ctrl+shift+k
        </div>
      </div>
    </div>
  );
}